import React from "react";
import { Check, X as XIcon } from "lucide-react";
import { useCMS, useSectionEnabled } from "../context/CMSContext";

function MobileStack({ compare, brand }) {
  const headers = compare.headers || [];
  const rows = compare.rows || [];
  const additional = compare.additional || [];

  return (
    <div className="md:hidden space-y-4">
      {headers.map((h, idx) => {
        const isUs = h.highlight;
        return (
          <div
            key={h.name}
            className={`rounded-2xl p-5 relative ${
              isUs ? "lm-dark-card" : "bg-white border border-[#11151C]/10"
            }`}
          >
            {isUs && (
              <span className="absolute -top-2.5 left-4 lm-popular-ribbon px-2.5 py-0.5 rounded-full text-[10px] font-bold">
                BEST VALUE
              </span>
            )}
            <div className="text-center mb-4 mt-2">
              <h3 className={`lm-font font-extrabold text-2xl ${isUs ? "text-white" : "text-[#11151C]"}`}>
                {h.name}
              </h3>
              <div
                className={`lm-font font-extrabold text-3xl mt-2 ${
                  isUs ? "text-[#FF6B3D]" : "text-[#11151C]"
                }`}
              >
                {h.price}
              </div>
              <p className={`text-xs mt-1 ${isUs ? "text-white/60" : "text-[#11151C]/55"}`}>
                {isUs ? `Truly unlimited hosting with autoscale resources` : "Compare features"}
              </p>
            </div>

            <div className={`rounded-xl p-3 ${isUs ? "bg-white/5" : "bg-[#FFFBF6]"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${
                isUs ? "text-white/70" : "text-[#11151C]/60"
              }`}>
                Resources
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {rows.map((r) => (
                  <div key={r.feature} className="flex flex-col">
                    <span className={isUs ? "text-white/60" : "text-[#11151C]/60"}>{r.feature}</span>
                    <span className={`font-bold ${isUs ? "text-white" : "text-[#11151C]"}`}>
                      {r.values?.[idx] || "\u2014"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div
                className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${
                  isUs ? "text-white/70" : "text-[#11151C]/60"
                }`}
              >
                Features
              </div>
              <ul className="space-y-1.5">
                {additional.map((r) => {
                  const has = !!r.values?.[idx];
                  return (
                    <li key={r.feature} className="flex items-center gap-2 text-xs">
                      {has ? (
                        <Check className="w-4 h-4 text-[#FF6B3D] flex-shrink-0" strokeWidth={3} />
                      ) : (
                        <XIcon className={`w-4 h-4 flex-shrink-0 ${isUs ? "text-white/30" : "text-[#11151C]/30"}`} />
                      )}
                      <span className={isUs ? "text-white/85" : "text-[#11151C]/80"}>{r.feature}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        );
      })}
      <p className="text-xs text-[#11151C]/50 text-center">*Prices based on annual billing</p>
    </div>
  );
}

export default function Compare() {
  const enabled = useSectionEnabled("compare");
  const { cms } = useCMS();
  if (!enabled) return null;
  const compare = cms?.compare || { headers: [], rows: [], additional: [] };
  const brand = cms?.site?.brandName || "Live Mover";

  return (
    <section id="compare" className="bg-white py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12 max-w-3xl mx-auto">
          <h2 className="lm-section-title text-2xl sm:text-4xl lg:text-5xl">{brand} vs Others</h2>
          <p className="text-[#11151C]/65 mt-3 text-base sm:text-lg">
            See how we stack up against the competition
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-[#11151C]/10">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-[#FFFBF6]">
                <th className="text-left p-4 sm:p-5 font-bold text-[#11151C]">Features</th>
                {(compare.headers || []).map((h) => (
                  <th
                    key={h.name}
                    className={`p-4 sm:p-5 text-center ${h.highlight ? "bg-[#11151C] text-white" : "text-[#11151C]"}`}
                  >
                    <div className="lm-font font-extrabold text-base sm:text-lg">{h.name}</div>
                    <div className={`text-xs sm:text-sm font-medium mt-1 ${h.highlight ? "text-[#FF6B3D]" : "text-[#11151C]/60"}`}>
                      {h.price}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(compare.rows || []).map((r, idx) => (
                <tr key={r.feature} className={idx % 2 === 0 ? "bg-white" : "bg-[#FFFBF6]/50"}>
                  <td className="p-3 sm:p-4 font-medium text-[#11151C]">{r.feature}</td>
                  {(r.values || []).map((v, i) => (
                    <td
                      key={i}
                      className={`p-3 sm:p-4 text-center ${
                        i === 0 ? "bg-[#FFF1E5] font-bold text-[#11151C]" : "text-[#11151C]/70"
                      }`}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td colSpan={6} className="p-3 sm:p-4 text-xs uppercase tracking-wider font-bold text-[#11151C]/50 bg-[#FFFBF6]">
                  Additional Features
                </td>
              </tr>
              {(compare.additional || []).map((r, idx) => (
                <tr key={r.feature} className={idx % 2 === 0 ? "bg-white" : "bg-[#FFFBF6]/50"}>
                  <td className="p-3 sm:p-4 font-medium text-[#11151C]">{r.feature}</td>
                  {(r.values || []).map((v, i) => (
                    <td key={i} className={`p-3 sm:p-4 text-center ${i === 0 ? "bg-[#FFF1E5]" : ""}`}>
                      {v ? (
                        <Check className="w-5 h-5 text-[#FF6B3D] inline" strokeWidth={3} />
                      ) : (
                        <XIcon className="w-5 h-5 text-[#11151C]/30 inline" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked */}
        <MobileStack compare={compare} brand={brand} />

        <p className="hidden md:block text-xs text-[#11151C]/50 mt-3">*Prices shown are based on annual billing plans</p>
      </div>
    </section>
  );
}
